import { z } from 'zod';
import { timestampSchema, type BusinessAction } from './assistant';
import { uuidV7Schema } from './ids';

export const outgoingLinkSchema = z
  .object({
    kind: z.enum(['action_confirmation', 'account_verification']),
    actionId: uuidV7Schema,
  })
  .strict();
export type OutgoingLink = z.infer<typeof outgoingLinkSchema>;

export const calendarBookingReceiptSchema = z
  .object({
    providerBookingId: z.string().min(1),
    status: z.enum(['confirmed', 'cancelled']),
    startsAt: timestampSchema,
    endsAt: timestampSchema,
    url: z.string().nullable(),
  })
  .strict();
export type CalendarBookingReceipt = z.infer<
  typeof calendarBookingReceiptSchema
>;

export const calendarBookingStateSchema = calendarBookingReceiptSchema
  .extend({ version: z.string().min(1), mutationId: z.string().nullable() })
  .strict();
export type StoredCalendarBookingState = z.infer<
  typeof calendarBookingStateSchema
>;
export type AppointmentChangeRequest = Extract<
  BusinessAction,
  { kind: 'cancel_appointment' | 'reschedule_appointment' }
>;

export const appointmentContextSchema = z
  .object({
    serviceId: uuidV7Schema.optional(),
    connectionScope: z.literal('account').optional(),
    adviserId: uuidV7Schema.optional(),
    bookingCalendarId: uuidV7Schema.optional(),
    poolId: uuidV7Schema.optional(),
    crmHost: z
      .object({ companyId: z.string().uuid(), employeeId: z.string().uuid() })
      .strict()
      .optional(),
    connectionId: uuidV7Schema,
    calendarId: z.string().min(1),
    timezone: z.string().min(1),
    businessConnectionId: uuidV7Schema.nullable(),
    appointmentKind: z
      .enum(['appointment', 'site_visit', 'sales_call'])
      .optional(),
  })
  .strict();
export type AppointmentContext = z.infer<typeof appointmentContextSchema>;
export const appointmentListSchema = z
  .object({
    appointments: z
      .array(
        z
          .object({
            actionId: uuidV7Schema,
            subject: z.string(),
            booking: calendarBookingStateSchema.nullable(),
          })
          .strict()
      )
      .max(25),
    nextCursor: uuidV7Schema.nullable(),
  })
  .strict();

export const MAX_ACTION_ATTEMPTS = 8;
export const holdTriageSchema = z.discriminatedUnion('operation', [
  z
    .object({
      operation: z.literal('create_case'),
      reference: z.string().trim().min(1).max(500),
      note: z.string().trim().min(1).max(2000),
    })
    .strict(),
  z
    .object({
      operation: z.literal('correct_false_positive'),
      note: z.string().trim().min(1).max(2000),
    })
    .strict(),
]);
export type HoldTriage = z.infer<typeof holdTriageSchema>;
export function actionRecoveryKind(
  action: BusinessAction
): 'replay' | 'reconcile' {
  switch (action.kind) {
    case 'search_offerings':
    case 'calendar_availability':
    case 'find_appointment_slots':
    case 'list_appointments':
    case 'read_receivables':
    case 'verify_account':
    case 'handoff':
      return 'replay';
    default:
      return 'reconcile';
  }
}

// Read-only refinements can repeat within the turn budget; workflow mutations cannot.
export function isReadOnlyAction(kind: BusinessAction['kind']): boolean {
  return (
    kind === 'search_offerings' ||
    kind === 'calendar_availability' ||
    kind === 'find_appointment_slots' ||
    kind === 'list_appointments' ||
    kind === 'read_receivables'
  );
}

type AppointmentBooking = Extract<
  BusinessAction,
  { kind: 'request_sales_appointment' | 'book_appointment' }
>;
const isAppointmentBooking = (
  action: BusinessAction
): action is AppointmentBooking =>
  action.kind === 'request_sales_appointment' ||
  action.kind === 'book_appointment';

// Confirmed by the customer and not yet acknowledged or given up.
const isUnderWay = (action: { status: string; confirmedAt: string | null }) =>
  action.confirmedAt !== null &&
  ['pending', 'executing', 'reconciliation_required'].includes(action.status);

// A booking, move or cancellation the customer authorised that is being
// carried out right now: queued or executing, with its outcome seconds away.
export function appointmentActionInProgress(action: {
  request: BusinessAction;
  status: string;
  confirmedAt: string | null;
}): boolean {
  return (
    (isAppointmentBooking(action.request) ||
      action.request.kind === 'reschedule_appointment' ||
      action.request.kind === 'cancel_appointment') &&
    action.confirmedAt !== null &&
    (action.status === 'pending' || action.status === 'executing')
  );
}

// The time of a booking or a move the customer authorised that is still being
// carried out. The calendar can already show it before its receipt exists.
export function appointmentUnderWay(action: {
  request: BusinessAction;
  status: string;
  confirmedAt: string | null;
}): { startsAt: string; endsAt: string } | null {
  const { request } = action;
  return (isAppointmentBooking(request) ||
    request.kind === 'reschedule_appointment') &&
    isUnderWay(action)
    ? { startsAt: request.startsAt, endsAt: request.endsAt }
    : null;
}

// A booking the customer authorised stays one request while it is carried
// out. Its wording changes between messages; the service and the time identify
// the appointment. The message that queued it is not a repetition of it: a
// retry of that turn continues its own request.
export function repeatsQueuedAppointment(
  queued: {
    request: BusinessAction;
    status: string;
    confirmedAt: string | null;
    sourceEventId: string;
  },
  proposed: BusinessAction,
  sourceEventId: string
): boolean {
  return (
    isAppointmentBooking(queued.request) &&
    isAppointmentBooking(proposed) &&
    queued.sourceEventId !== sourceEventId &&
    isUnderWay(queued) &&
    (queued.request.serviceId ?? null) === (proposed.serviceId ?? null) &&
    Date.parse(queued.request.startsAt) === Date.parse(proposed.startsAt) &&
    Date.parse(queued.request.endsAt) === Date.parse(proposed.endsAt)
  );
}

// Structured property searches are defined by criteria; descriptive wording is not a filter.
export function actionIdentity(action: BusinessAction): string {
  if (action.kind === 'find_appointment_slots')
    return JSON.stringify({
      kind: action.kind,
      serviceId: action.serviceId ?? null,
      localDate: action.localDate,
      weekday: action.weekday,
      localTime: action.localTime ?? null,
      timeWindow: action.timeWindow
        ? {
            startsAt: action.timeWindow.startsAt,
            endsAt: action.timeWindow.endsAt,
          }
        : null,
      timeOfDay: action.timeOfDay,
      offset: action.offset,
    });
  if (action.kind === 'cancel_appointment')
    return JSON.stringify({
      kind: action.kind,
      appointmentActionId: action.appointmentActionId,
      expectedVersion: action.expectedVersion,
    });
  if (action.kind !== 'search_offerings' || !action.propertySearch)
    return JSON.stringify(action);
  const alternatives = action.propertySearch.alternatives.map(
    (alternative) => ({
      ...alternative,
      amenities: alternative.amenities
        ? [...new Set(alternative.amenities)].sort()
        : null,
      ...(alternative.unitTypes
        ? { unitTypes: [...new Set(alternative.unitTypes)].sort() }
        : {}),
    })
  );
  return JSON.stringify({
    kind: action.kind,
    propertySearch: {
      alternatives,
      unsupportedCriteria: [
        ...new Set(action.propertySearch.unsupportedCriteria),
      ].sort(),
    },
  });
}
